import { buildAuthedImageUrl, useCanWrite } from '@artic/api-client';
import { Badge } from '@artic/ui/badge';
import { Button } from '@artic/ui/button';
import { Lightbox, type LightboxSlide } from '@artic/ui/lightbox';
import { Textarea } from '@artic/ui/textarea';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { formatDistanceToNow, intervalToDuration } from 'date-fns';
import { useQueryStates } from 'nuqs';
import { useEffect, useState } from 'react';

import { useLightboxLabels } from '../../components/use-lightbox-labels';
import { usePartnerName } from '../../features/inbox/use-partner-name';
import {
  formatCriterionName,
  formatEffortLabel,
  formatResponseTypeLabel,
} from '../../features/vision-test-plans/labels';
import {
  VISION_TEST_PLANS_KEY,
  useStartRun,
  useUpdateVerdict,
  visionTestPlanItemsQueryOptions,
  visionTestPlanRunDetailQueryOptions,
  visionTestPlanRunResultsQueryOptions,
} from '../../features/vision-test-plans/queries';
import {
  RESULTS_DEFAULT_LIMIT,
  visionTestPlanRunResultsSearchParsers,
} from '../../features/vision-test-plans/search';
import type {
  AiResponse,
  VisionTestPlanItem,
  VisionTestPlanRunDetail,
  VisionTestPlanRunResult,
  VisionTestPlanRunStatus,
  VisionTestPlanVerdict,
} from '../../features/vision-test-plans/types';

export const Route = createFileRoute('/vision_test_plans/$test_plan_id_/runs/$run_id')({
  staticData: {
    breadcrumb: 'Run',
    layout: 'default',
  },
  component: VisionTestPlanRunDetailPage,
});

const STATUS_VARIANT: Record<
  VisionTestPlanRunStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  new: 'outline',
  queued: 'secondary',
  in_progress: 'default',
  completed: 'default',
  error: 'destructive',
};

// ─── Utils ────────────────────────────────────────────────────────────────────

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return '—';
  const end = completedAt ? new Date(completedAt) : null;
  if (!end) return 'In progress';
  const dur = intervalToDuration({ start: new Date(startedAt), end });
  const parts: string[] = [];
  if (dur.hours) parts.push(`${dur.hours}h`);
  if (dur.minutes) parts.push(`${dur.minutes}m`);
  parts.push(`${dur.seconds ?? 0}s`);
  return parts.join(' ');
}

function inferExpectedVerdict(
  run: Pick<VisionTestPlanRunDetail, 'expected_outcome_all_pass' | 'expected_outcome_all_fail'>,
  aiPassed: boolean | undefined,
): VisionTestPlanVerdict | null {
  if (aiPassed === undefined) return null;
  if (run.expected_outcome_all_pass) return aiPassed ? 'correct' : 'incorrect';
  if (run.expected_outcome_all_fail) return aiPassed ? 'incorrect' : 'correct';
  return null;
}

// ─── Thumbnail ────────────────────────────────────────────────────────────────

function buildImageSrc(storagePath: string, partnerName: string): string {
  const encodedPath = storagePath
    .split('/')
    .map((s) => encodeURIComponent(s))
    .join('/');
  return (
    buildAuthedImageUrl(
      `/quality/evidence/file/${encodeURIComponent(partnerName)}/${encodedPath}`,
    ) ?? ''
  );
}

function ResultThumbnail({ src, onClick }: { src: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-muted flex h-14 w-14 shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded transition-opacity hover:opacity-80"
      aria-label="View full-size image"
    >
      {src ? (
        <img src={src} alt="evidence" className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <span className="text-muted-foreground text-xs">—</span>
      )}
    </button>
  );
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────

interface LightboxImage {
  url: string;
  filename: string;
  result: VisionTestPlanRunResult | null;
}

function LightboxFooter({
  result,
  partnerName,
  testPlanId,
  runId,
}: {
  result: VisionTestPlanRunResult;
  partnerName: string;
  testPlanId: string;
  runId: string;
}) {
  const mutation = useUpdateVerdict();
  const canWrite = useCanWrite('quality');
  const aiResponse = result.ai_response as AiResponse | null;

  function handleVerdict(v: VisionTestPlanVerdict) {
    if (!canWrite) return;
    const next: VisionTestPlanVerdict | null = result.verdict === v ? null : v;
    mutation.mutate({
      partnerName,
      testPlanId,
      runId,
      resultId: result.result_id,
      verdict: next,
    });
  }

  return (
    <div className="flex items-center justify-center gap-0 px-4 py-3">
      <div className="flex flex-wrap items-center justify-end gap-2 pr-4">
        {aiResponse ? (
          <>
            <Badge
              variant={aiResponse.passed ? 'secondary' : 'destructive'}
              className={
                aiResponse.passed
                  ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-200'
                  : ''
              }
            >
              {aiResponse.passed ? 'Pass' : 'Fail'} {aiResponse.score}/{aiResponse.total_checks}
            </Badge>
            {(aiResponse.results ?? []).map((step) => {
              const passed = step.response_type === 'text' ? true : step.response === 'yes';
              return (
                <span
                  key={step.label}
                  title={step.prompt}
                  className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
                    passed
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                      : 'bg-destructive/10 text-destructive dark:bg-destructive/20'
                  }`}
                >
                  {formatCriterionName(step.label)}
                </span>
              );
            })}
          </>
        ) : (
          <span className="text-sm text-white/40">No result</span>
        )}
      </div>
      <div className="mx-0 w-px self-stretch bg-white/30" />
      <div className="flex shrink-0 gap-2 pl-4">
        <Button
          size="default"
          variant="outline"
          disabled={mutation.isPending || !canWrite}
          className={`min-w-[90px] font-medium ${
            result.verdict === 'correct'
              ? 'border-green-700 bg-green-200 text-green-900 hover:bg-green-300 dark:border-green-600 dark:bg-green-900/50 dark:text-green-100 dark:hover:bg-green-900/70'
              : 'border-green-600 text-green-700 hover:bg-green-100 hover:text-green-800 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/30 dark:hover:text-green-200'
          }`}
          onClick={() => handleVerdict('correct')}
        >
          Agree
        </Button>
        <Button
          size="default"
          variant="outline"
          disabled={mutation.isPending || !canWrite}
          className={`min-w-[90px] font-medium ${
            result.verdict === 'incorrect'
              ? 'border-destructive bg-destructive/20 text-destructive hover:bg-destructive/30 dark:bg-destructive/30 dark:hover:bg-destructive/40'
              : 'border-destructive/50 text-destructive hover:bg-destructive/10 dark:border-destructive/40 dark:hover:bg-destructive/20'
          }`}
          onClick={() => handleVerdict('incorrect')}
        >
          Disagree
        </Button>
      </div>
    </div>
  );
}

// ─── AI result summary ────────────────────────────────────────────────────────

function AiResultSummary({ aiResponse }: { aiResponse: AiResponse | null }) {
  if (!aiResponse) return <span className="text-muted-foreground text-xs">—</span>;

  return (
    <div className="flex flex-col gap-1">
      <Badge
        variant={aiResponse.passed ? 'secondary' : 'destructive'}
        className={
          aiResponse.passed
            ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-200'
            : ''
        }
      >
        {aiResponse.passed ? 'Pass' : 'Fail'} {aiResponse.score}/{aiResponse.total_checks}
      </Badge>
      <div className="flex flex-wrap gap-1">
        {(aiResponse.results ?? []).map((step) => {
          const passed = step.response_type === 'text' ? true : step.response === 'yes';
          return (
            <span
              key={step.label}
              title={step.prompt}
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
                passed
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                  : 'bg-destructive/10 text-destructive dark:bg-destructive/20'
              }`}
            >
              {formatCriterionName(step.label)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── Verdict cell ─────────────────────────────────────────────────────────────

function VerdictCell({
  result,
  partnerName,
  testPlanId,
  runId,
  expectedVerdict,
}: {
  result: VisionTestPlanRunResult;
  partnerName: string;
  testPlanId: string;
  runId: string;
  expectedVerdict: VisionTestPlanVerdict | null;
}) {
  const [notes, setNotes] = useState(result.verdict_notes ?? '');
  const [showNotes, setShowNotes] = useState(!!result.verdict_notes);
  const mutation = useUpdateVerdict();
  const canWrite = useCanWrite('quality');

  if (result.status === 'queued' || result.status === 'processing') {
    return <span className="text-muted-foreground text-xs">Pending…</span>;
  }

  function handleVerdict(v: VisionTestPlanVerdict) {
    if (!canWrite) return;
    const next: VisionTestPlanVerdict | null = result.verdict === v ? null : v;
    if (next) setShowNotes(true);
    mutation.mutate({
      partnerName,
      testPlanId,
      runId,
      resultId: result.result_id,
      verdict: next,
      ...(notes ? { verdict_notes: notes } : {}),
    });
  }

  function handleNotesBlur() {
    if (!canWrite) return;
    if (result.verdict) {
      mutation.mutate({
        partnerName,
        testPlanId,
        runId,
        resultId: result.result_id,
        verdict: result.verdict,
        ...(notes ? { verdict_notes: notes } : {}),
      });
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {expectedVerdict && !result.verdict && (
        <p className="text-muted-foreground text-[10px]">
          Expected: {expectedVerdict === 'correct' ? '✓ Correct' : '✗ Incorrect'}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          size="default"
          variant="outline"
          className={`min-w-[90px] font-medium ${
            result.verdict === 'correct'
              ? 'border-green-700 bg-green-200 text-green-900 hover:bg-green-300 dark:border-green-600 dark:bg-green-900/50 dark:text-green-100 dark:hover:bg-green-900/70'
              : 'border-green-600 text-green-700 hover:bg-green-100 hover:text-green-800 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/30 dark:hover:text-green-200'
          }`}
          disabled={mutation.isPending || !canWrite}
          onClick={() => {
            handleVerdict('correct');
          }}
        >
          Agree
        </Button>
        <Button
          size="default"
          variant="outline"
          className={`min-w-[90px] font-medium ${
            result.verdict === 'incorrect'
              ? 'border-destructive bg-destructive/20 text-destructive hover:bg-destructive/30 dark:bg-destructive/30 dark:hover:bg-destructive/40'
              : 'border-destructive/50 text-destructive hover:bg-destructive/10 dark:border-destructive/40 dark:hover:bg-destructive/20'
          }`}
          disabled={mutation.isPending || !canWrite}
          onClick={() => {
            handleVerdict('incorrect');
          }}
        >
          Disagree
        </Button>
        {result.verdict && !showNotes && canWrite && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={() => {
              setShowNotes(true);
            }}
          >
            Add note
          </Button>
        )}
      </div>

      {(showNotes || result.verdict_notes) && result.status === 'done' && (
        <Textarea
          className="min-h-0 max-w-[180px] resize-none py-1 text-xs"
          rows={2}
          placeholder="Notes…"
          value={notes}
          disabled={!canWrite}
          onChange={(e) => {
            setNotes(e.target.value);
          }}
          onBlur={handleNotesBlur}
        />
      )}
    </div>
  );
}

// ─── Outcome summary banner ───────────────────────────────────────────────────

function OutcomeSummaryBanner({ run }: { run: VisionTestPlanRunDetail }) {
  if (run.status !== 'completed') return null;
  if (!run.expected_outcome_all_pass && !run.expected_outcome_all_fail) return null;

  let message: string;
  let isDeviation: boolean;

  if (run.expected_outcome_all_pass) {
    isDeviation = run.failed_items > 0;
    message = isDeviation
      ? `${run.failed_items} check${run.failed_items !== 1 ? 's' : ''} failed when all should pass`
      : `All ${run.total_items ?? run.passed_items} checks passed as expected`;
  } else {
    isDeviation = run.passed_items > 0;
    message = isDeviation
      ? `${run.passed_items} check${run.passed_items !== 1 ? 's' : ''} passed when all should fail`
      : `All ${run.total_items ?? run.failed_items} checks failed as expected`;
  }

  return (
    <div
      className={`rounded border px-4 py-3 text-sm font-medium ${
        isDeviation
          ? 'border-destructive/20 bg-destructive/10 text-destructive dark:border-destructive/30 dark:bg-destructive/20'
          : 'border-green-200 bg-green-100 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200'
      }`}
    >
      {message}
    </div>
  );
}

// ─── Run summary card ─────────────────────────────────────────────────────────

function RunSummaryCard({
  run,
  partnerName,
  testPlanId,
}: {
  run: VisionTestPlanRunDetail;
  partnerName: string;
  testPlanId: string;
}) {
  const startMutation = useStartRun();
  const canWrite = useCanWrite('quality');
  const pct =
    run.total_items && run.total_items > 0 ? (run.completed_items / run.total_items) * 100 : 0;

  return (
    <div className="bg-muted/40 border-border space-y-4 rounded border p-4">
      {/* Status + progress */}
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant={STATUS_VARIANT[run.status] ?? 'outline'} className="px-2 py-0.5 text-sm">
          {run.status}
        </Badge>
        {run.status === 'new' && canWrite && (
          <Button
            size="sm"
            disabled={startMutation.isPending}
            onClick={() => {
              startMutation.mutate({ partnerName, testPlanId, runId: run.run_id });
            }}
          >
            {startMutation.isPending ? 'Starting…' : 'Start Run'}
          </Button>
        )}
        {run.total_items != null && run.total_items > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-green-700">{run.passed_items} pass</span>
            <span className="text-muted-foreground">/</span>
            <span className="text-destructive font-medium">{run.failed_items} fail</span>
            <span className="text-muted-foreground">/</span>
            <span className="text-muted-foreground">{run.total_items} total</span>
          </div>
        )}
        {run.total_items != null && run.total_items > 0 && (
          <div className="bg-muted h-2 w-32 overflow-hidden rounded-full">
            <div
              className={`h-full rounded-full transition-all ${run.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>

      {/* Timing + model */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div>
          <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
            Started
          </p>
          <p className="mt-0.5 text-sm">
            {run.started_at
              ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true })
              : '—'}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
            Finished
          </p>
          <p className="mt-0.5 text-sm">
            {run.completed_at
              ? formatDistanceToNow(new Date(run.completed_at), { addSuffix: true })
              : run.started_at
                ? 'In progress'
                : '—'}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
            Duration
          </p>
          <p className="mt-0.5 text-sm">{formatDuration(run.started_at, run.completed_at)}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
            Effort
          </p>
          <p className="mt-0.5 text-sm">
            {run.model_override ? formatEffortLabel(run.model_override) : 'Set per Criterion'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Review stats bar ─────────────────────────────────────────────────────────

function ReviewStatsBar({
  run,
  results,
  partnerName,
  testPlanId,
  runId,
}: {
  run: VisionTestPlanRunDetail;
  results: VisionTestPlanRunResult[];
  partnerName: string;
  testPlanId: string;
  runId: string;
}) {
  const queryClient = useQueryClient();
  const mutation = useUpdateVerdict();
  const canWrite = useCanWrite('quality');
  const [isApplying, setIsApplying] = useState(false);

  const hasExpectedOutcome = run.expected_outcome_all_pass || run.expected_outcome_all_fail;
  const doneResults = results.filter((r) => r.status === 'done');
  const correctCount = run.correct_count ?? 0;
  const incorrectCount = run.incorrect_count ?? 0;
  const unreviewedCount = run.unreviewed_count ?? 0;
  const reviewedCount = correctCount + incorrectCount;
  const totalDone = doneResults.length;
  const reviewPct = totalDone > 0 ? (reviewedCount / totalDone) * 100 : 0;

  async function applyExpectedOutcome() {
    if (!canWrite) return;
    const unverdicted = results.filter((r) => r.status === 'done' && !r.verdict);
    if (!unverdicted.length) return;

    setIsApplying(true);
    try {
      for (const r of unverdicted) {
        const aiPassed = (r.ai_response as AiResponse | null)?.passed;
        const expected = inferExpectedVerdict(run, aiPassed);
        if (!expected) continue;
        await mutation.mutateAsync({
          partnerName,
          testPlanId,
          runId,
          resultId: r.result_id,
          verdict: expected,
        });
      }
      await queryClient.invalidateQueries({
        queryKey: [VISION_TEST_PLANS_KEY, 'run', partnerName, testPlanId, runId],
      });
    } finally {
      setIsApplying(false);
    }
  }

  if (totalDone === 0 && !hasExpectedOutcome) return null;

  return (
    <div className="border-border space-y-3 rounded border p-4">
      {totalDone > 0 && (
        <div className="flex flex-wrap items-center gap-4">
          <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
            Human review
          </p>
          <div className="flex items-center gap-3 text-sm">
            <span className="font-medium text-green-700">{correctCount} correct</span>
            <span className="text-destructive font-medium">{incorrectCount} incorrect</span>
            <span className="text-muted-foreground">{unreviewedCount} unreviewed</span>
          </div>
          {totalDone > 0 && (
            <div className="flex items-center gap-2">
              <div className="bg-muted h-1.5 w-24 overflow-hidden rounded-full">
                <div
                  className="h-full rounded-full bg-blue-500 transition-all"
                  style={{ width: `${reviewPct}%` }}
                />
              </div>
              <span className="text-muted-foreground text-xs">
                {reviewedCount}/{totalDone} reviewed
              </span>
            </div>
          )}
        </div>
      )}

      {hasExpectedOutcome && (
        <div className="flex flex-wrap items-center gap-3">
          <Badge
            variant="outline"
            className={
              run.expected_outcome_all_pass
                ? 'border-green-600 text-green-700 dark:border-green-700 dark:text-green-400'
                : 'border-destructive/50 text-destructive'
            }
          >
            Expected: {run.expected_outcome_all_pass ? 'All Pass' : 'All Fail'}
          </Badge>
          <p className="text-muted-foreground text-xs">
            Human verdicts are inferred from the expected outcome.
          </p>
          {unreviewedCount > 0 && canWrite && (
            <Button
              size="sm"
              variant="outline"
              disabled={isApplying || mutation.isPending}
              onClick={() => {
                void applyExpectedOutcome();
              }}
            >
              {isApplying ? 'Applying…' : `Apply expected outcome verdicts (${unreviewedCount})`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Results table row type ───────────────────────────────────────────────────

interface ResultRow {
  storagePath: string;
  result: VisionTestPlanRunResult | null;
}

// ─── Results table ────────────────────────────────────────────────────────────

function ResultsTable({
  rows,
  run,
  partnerName,
  testPlanId,
  runId,
}: {
  rows: ResultRow[];
  run: VisionTestPlanRunDetail | undefined;
  partnerName: string;
  testPlanId: string;
  runId: string;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const labels = useLightboxLabels();

  const lightboxImages: LightboxImage[] = rows.map(({ storagePath, result }) => ({
    url: buildImageSrc(storagePath, partnerName),
    filename: storagePath.split('/').pop() ?? storagePath,
    result,
  }));
  const lightboxSlides: LightboxSlide[] = lightboxImages.map((image) => ({
    src: image.url,
    title: image.filename,
  }));

  if (rows.length === 0) {
    return (
      <div className="border-border rounded border border-dashed p-8 text-center">
        <p className="text-muted-foreground text-sm">No items found in this test plan.</p>
      </div>
    );
  }

  return (
    <>
      <Lightbox
        slides={lightboxSlides}
        open={lightboxIndex !== null}
        index={lightboxIndex ?? 0}
        onOpenChange={(open) => {
          if (!open) setLightboxIndex(null);
        }}
        onIndexChange={setLightboxIndex}
        labels={labels.lightbox}
        // The verdict footer rides inside the lightbox so a reviewer can agree or
        // disagree with the AI while looking at the full-size photo.
        renderFooter={(_slide, index) => {
          const result = lightboxImages[index]?.result;
          if (!result || (result.status !== 'done' && result.status !== 'failed')) return null;
          return (
            <LightboxFooter
              result={result}
              partnerName={partnerName}
              testPlanId={testPlanId}
              runId={runId}
            />
          );
        }}
      />
      <div className="border-border overflow-x-auto rounded border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Image</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">This run</th>
              <th className="px-3 py-2 font-medium">Human verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ storagePath, result }, idx) => {
              const aiPassed = (result?.ai_response as AiResponse | null)?.passed;
              const expectedVerdict = run ? inferExpectedVerdict(run, aiPassed) : null;
              const src = buildImageSrc(storagePath, partnerName);

              return (
                <tr key={result?.result_id ?? idx} className="border-border border-t align-top">
                  <td className="px-3 py-2">
                    <ResultThumbnail src={src} onClick={() => setLightboxIndex(idx)} />
                  </td>
                  <td className="px-3 py-2">
                    {result ? (
                      <>
                        <Badge
                          variant={
                            result.status === 'done'
                              ? 'secondary'
                              : result.status === 'failed'
                                ? 'destructive'
                                : 'secondary'
                          }
                          className={
                            result.status === 'done'
                              ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-200'
                              : ''
                          }
                        >
                          {result.status}
                        </Badge>
                        {result.error && (
                          <p className="text-destructive mt-1 line-clamp-2 max-w-[150px] text-xs">
                            {result.error}
                          </p>
                        )}
                      </>
                    ) : (
                      <Badge variant="outline">pending</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {result ? (
                      <AiResultSummary aiResponse={result.ai_response} />
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {result ? (
                      <VerdictCell
                        result={result}
                        partnerName={partnerName}
                        testPlanId={testPlanId}
                        runId={runId}
                        expectedVerdict={expectedVerdict}
                      />
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function VisionTestPlanRunDetailPage() {
  const { test_plan_id: testPlanId, run_id: runId } = Route.useParams();
  const partnerName = usePartnerName();
  const navigate = useNavigate();

  const [{ resultsPage }, setResultsSearch] = useQueryStates(visionTestPlanRunResultsSearchParsers);

  const { data: run, isLoading: isRunLoading } = useQuery(
    visionTestPlanRunDetailQueryOptions(partnerName, testPlanId, runId),
  );

  // If the URL has a stale/missing test_plan_id (e.g. "undefined"), redirect once
  // the run loads and we know the real plan ID.
  useEffect(() => {
    if (run && run.test_plan_id && run.test_plan_id !== testPlanId) {
      void navigate({
        to: '/vision_test_plans/$test_plan_id/runs/$run_id',
        params: { test_plan_id: run.test_plan_id, run_id: runId },
        replace: true,
      });
    }
  }, [run, testPlanId, runId, navigate]);

  const isNewRun = run?.status === 'new';

  // For "new" runs (no result rows yet), drive the table from items.
  // For all other statuses, drive from paginated results (which include storage_path).
  const { data: itemsData } = useQuery({
    ...visionTestPlanItemsQueryOptions(partnerName, testPlanId),
    enabled: !!partnerName && !!testPlanId && isNewRun,
  });

  const { data: resultsPage_data } = useQuery(
    visionTestPlanRunResultsQueryOptions(
      partnerName,
      testPlanId,
      runId,
      resultsPage,
      RESULTS_DEFAULT_LIMIT,
    ),
  );

  const results = resultsPage_data?.data ?? [];
  const resultsMeta = resultsPage_data?.meta;
  const totalPages = resultsMeta ? Math.ceil(resultsMeta.total / resultsMeta.limit) : 1;

  // Build rows for the table
  const tableRows: ResultRow[] = isNewRun
    ? (itemsData ?? []).map((item: VisionTestPlanItem) => ({
        storagePath: item.storage_path,
        result: null,
      }))
    : results.map((r) => ({
        storagePath: r.storage_path,
        result: r,
      }));

  return (
    <div className="flex flex-col gap-4 p-6">
      <header>
        <Link
          to="/vision_test_plans/$test_plan_id"
          params={{ test_plan_id: testPlanId }}
          className="text-muted-foreground mb-1 inline-block text-xs hover:underline"
        >
          {run ? `← ${run.plan_name}` : '← Back to plan'}
        </Link>
        <h1 className="text-2xl font-semibold">{run?.name ?? 'Run Detail'}</h1>
        {run?.notes && <p className="text-muted-foreground mt-1 text-sm">{run.notes}</p>}
      </header>

      {isRunLoading ? (
        <div className="bg-muted/40 border-border h-32 animate-pulse rounded border" />
      ) : run && partnerName ? (
        <RunSummaryCard run={run} partnerName={partnerName} testPlanId={testPlanId} />
      ) : null}

      {run && <OutcomeSummaryBanner run={run} />}

      {run && run.prompts && run.prompts.length > 0 && (
        <section>
          <h2 className="mb-3 text-lg font-medium">Acceptance Criteria</h2>
          <div className="flex flex-col gap-3">
            {run.prompts.map((p, i) => (
              <div key={i} className="border-border rounded border p-4">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="text-base font-semibold">{formatCriterionName(p.label)}</span>
                  <Badge variant="secondary">{formatResponseTypeLabel(p.response_type)}</Badge>
                  {p.model && <Badge variant="outline">{formatEffortLabel(p.model)}</Badge>}
                  {run.model_override && !p.model && (
                    <Badge variant="outline">{formatEffortLabel(run.model_override)}</Badge>
                  )}
                </div>
                <p className="text-muted-foreground">{p.prompt}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {run && partnerName && results.length > 0 && (
        <ReviewStatsBar
          run={run}
          results={results}
          partnerName={partnerName}
          testPlanId={testPlanId}
          runId={runId}
        />
      )}

      {partnerName && (
        <>
          <ResultsTable
            rows={tableRows}
            run={run}
            partnerName={partnerName}
            testPlanId={testPlanId}
            runId={runId}
          />

          {!isNewRun && resultsMeta && resultsMeta.total > resultsMeta.limit && (
            <div className="flex items-center justify-between text-sm">
              <Button
                variant="outline"
                size="sm"
                disabled={resultsPage <= 1}
                onClick={() => {
                  void setResultsSearch({ resultsPage: resultsPage - 1 });
                }}
              >
                Previous
              </Button>
              <span className="text-muted-foreground">
                Page {resultsPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={resultsPage >= totalPages}
                onClick={() => {
                  void setResultsSearch({ resultsPage: resultsPage + 1 });
                }}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
